import type { BillingPaymentMethod } from "../../shared/billing-action-schemas";
import { SelectField, TextField } from "./billing-fields";
import { formatDay } from "./billing-format";
import { PAYMENT_METHOD_LABEL, type RowAction, type RowActionDraft } from "./billing-row-actions";
import { fieldState, type FormErrors } from "./billing-validation";

/** The fields one row action asks for, beyond the reason every action takes (ADR-0073). */

type ActionFieldsProps = {
  action: RowAction;
  draft: RowActionDraft;
  update: (patch: Partial<RowActionDraft>) => void;
  errors: FormErrors;
  requirements: Record<string, string>;
};

const CLOSE_NOTE = {
  void: "Stripe marks the invoice void and stops asking for payment. Use this for an invoice sent in error.",
  write_off:
    "Stripe marks the invoice uncollectible and stops asking for payment. Use this when it will never be paid.",
};

function PaymentFields({ action, draft, update, errors, requirements }: ActionFieldsProps) {
  const state = (name: string) => fieldState(errors, requirements, name, `row-${name}`);
  const methods = Object.entries(PAYMENT_METHOD_LABEL).map(([value, label]) => ({ value, label }));
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <TextField
        {...state("day")}
        label="Paid on"
        type="date"
        value={draft.day}
        required
        onChange={(day) => {
          update({ day });
        }}
      />
      <SelectField
        {...state("method")}
        label={action.kind === "paid" ? "Paid by (optional)" : "Paid by"}
        value={draft.method}
        options={
          action.kind === "paid" ? [{ value: "", label: "Not recorded" }, ...methods] : methods
        }
        onChange={(method) => {
          update({ method: method as BillingPaymentMethod | "" });
        }}
      />
    </div>
  );
}

function NextInvoiceField({ action, draft, update, errors, requirements }: ActionFieldsProps) {
  const current = "subscription" in action ? action.subscription.currentPeriodEnd : null;
  return (
    <div className="flex flex-col gap-2">
      <TextField
        {...fieldState(errors, requirements, "day", "row-day")}
        label="Next invoice on"
        type="date"
        value={draft.day}
        required
        onChange={(day) => {
          update({ day });
        }}
      />
      {current === null ? null : (
        <p className="text-sm text-muted-foreground">Today it is {formatDay(current)}.</p>
      )}
    </div>
  );
}

export function ActionFields(props: ActionFieldsProps) {
  const { action, draft, update, errors, requirements } = props;
  const state = (name: string) => fieldState(errors, requirements, name, `row-${name}`);
  switch (action.kind) {
    case "refund":
      return (
        <TextField
          {...state("amount")}
          label="Amount (USD, blank for everything still refundable)"
          inputMode="decimal"
          value={draft.amount}
          onChange={(amount) => {
            update({ amount });
          }}
        />
      );
    case "paid":
    case "paid_outside":
      return <PaymentFields {...props} />;
    case "void":
    case "write_off":
      return <p className="text-sm text-muted-foreground">{CLOSE_NOTE[action.kind]}</p>;
    case "next_invoice":
      return <NextInvoiceField {...props} />;
    case "status":
      return (
        <SelectField
          {...state("status")}
          label="New status"
          value={draft.status}
          options={[
            { value: "past_due", label: "Past due" },
            { value: "void", label: "Void" },
            { value: "uncollectible", label: "Uncollectible" },
          ]}
          onChange={(status) => {
            update({ status: status as RowActionDraft["status"] });
          }}
        />
      );
    case "cancel":
      return action.subscription.source === "stripe" ? (
        <SelectField
          {...state("when")}
          label="When"
          value={draft.atPeriodEnd ? "period_end" : "now"}
          options={[
            { value: "period_end", label: "At the end of the current period" },
            { value: "now", label: "Immediately" },
          ]}
          onChange={(when) => {
            update({ atPeriodEnd: when === "period_end" });
          }}
        />
      ) : null;
    default:
      return null;
  }
}
